import { ReactNode, useCallback } from "react";
import { DefaultButton } from "@fluentui/react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "../../components/Card"
import { CardContent } from "../../components/CardContent";
import { CardHeader } from "../../components/CardHeader"
import { Instructions } from "../../components/Instructions";
import { PageContainer } from "../../components/PageContainer";
import { PageHeader } from "../../components/PageHeader";
import { AppRoutes } from "../../constants/AppRoutes";
import { FormStatus, formStatusToString } from "../../data/models/Form";
import { TaskOutcome } from "../../data/models/Task";
import { GetForm } from "../../data/queries/FormQueries";
import { GetTasks } from "../../data/queries/TaskQueries";
import { QueryKeys } from "../../data/QueryKeys";
import { VPICReimbursementForm } from "./VPICReimbursementForm"
import { DetailsCardShimmer, FormCardShimmer } from "../../helpers/Shimmer";
import { TaskHistoryList } from "../../components/TaskHistoryList";
import { AccountingPaymentStage, ClarificationStage, ComplianceApprovalStage, FinanceApprovalStage, NoActionsStage, submitForm } from "../../forms/DefaultStages";
import { FormVpicReimbursement } from "../../data/models/FormVpicReimbursement";
import { TaskVpicReimbursement } from "../../data/models/TaskVpicReimbursement";
import { useUserContext } from "../../hooks/useUserContext";
import { hasPermission, hasRole, UserRole } from "../../data/models/User";
import { VPICReimbursementComplianceForm } from "./VPICReimbursementComplianceForm";
import { FormVpicPreApproval } from "../../data/models/FormVpicPreApproval";
import { VPICReimbursementDetails } from "./VPICReimbursementDetails";
import { Flex } from "../../components/Flex";
import { VPIDisplayTextBox } from "../../fields/VPIDisplayTextBox";
import { currencyToString } from "../../utilities/FormUtils";

const FORM_TYPE = 'vpic-reimbursement';
const INSTRUCTIONS_URL = '/instructions/VPIC - Reimbursement Instructions.pdf';

export const VPICReimbursementWorkflow = (props: any) => 
{
    const { formId } = useParams();
    const { user } = useUserContext();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const nav = useNavigate();
    const isNewForm = formId === 'new';

    const formQuery = useQuery(QueryKeys.VPICReimbursement.Form(formId || ''), async () => GetForm<FormVpicReimbursement>(FORM_TYPE, formId || ''), { enabled: !isNewForm });
    const taskQuery = useQuery(QueryKeys.VPICReimbursement.Tasks(formId || ''), async () => GetTasks<TaskVpicReimbursement>(FORM_TYPE, formId || ''), { enabled: !isNewForm });
    const preApprovalFormId = formQuery.data?.preApprovalFormId || searchParams.get('p') || '';
    const preApprovalFormQuery = useQuery(QueryKeys.VPICPreApproval.Form(preApprovalFormId), () => GetForm<FormVpicPreApproval>('vpic-preapproval', preApprovalFormId), { enabled: formQuery.isSuccess && !!preApprovalFormId });
    const isLoaded = isNewForm || (formQuery.isSuccess && taskQuery.isSuccess && (preApprovalFormQuery.isSuccess || !preApprovalFormId));

    let detailsView: ReactNode = <DetailsCardShimmer />;
    let formView: ReactNode = <FormCardShimmer />;

    const form = formQuery.data;
    const tasks = taskQuery.data;
    const preApprovalForm = preApprovalFormQuery.data;

    const refreshPage = useCallback(() => { 
        queryClient.invalidateQueries(QueryKeys.VPICReimbursement.Form(formId || '')); 
        nav(AppRoutes.Dashboard()); 
    }, [queryClient, formId]);

    const onSaveForm = useCallback(async (formData: Partial<FormVpicReimbursement>, action: 'save-draft' | 'submit') => {
        try
        {
            await submitForm(FORM_TYPE, form?.id, formData, action);
            refreshPage();
        }
        catch(error: any)
        {
            console.log(error);
            alert(JSON.stringify(error.response.data));
        }
    }, [form, refreshPage]);

    if(isLoaded)
    {
        if(isNewForm)
        {
            detailsView = <Instructions url={INSTRUCTIONS_URL} />;
            formView = (
                <>
                    <CardHeader>VPIC - Reimbursement Request</CardHeader>
                    <CardContent>
                        <VPICReimbursementForm onSave={onSaveForm} />
                    </CardContent>
                </>
            );
        }
        // If a form item has already been created and is fetched (i.e. not new)
        else if(form && tasks)
        {
            const currentTask = tasks.find(t => !t.outcome || t.outcome === TaskOutcome.InProgress);
    
            switch(form.formStatus)
            {
                case FormStatus.Draft:
                case FormStatus.AdvisorRevision:
                    detailsView = <Instructions url={INSTRUCTIONS_URL} />;
                    break;
                case FormStatus.AdvisorClarification:
                case FormStatus.ComplianceApproval:
                case FormStatus.ComplianceClarification:
                case FormStatus.FinanceApproval:
                case FormStatus.Completed:
                    detailsView = <VPICReimbursementDetails form={form} preApprovalForm={preApprovalForm} variant="2column" />
                    break;
                default:
                    detailsView = null;
                    break;
            }
    
            // Set the form view based on form status and user role
            if((form.formStatus === FormStatus.Draft || form.formStatus === FormStatus.AdvisorRevision) && hasPermission(user, 'vpic.write'))
                formView = (
                    <>
                        <CardHeader>VPIC - Reimbursement Request</CardHeader>
                        <CardContent>
                            <VPICReimbursementForm form={form} onSave={onSaveForm} allowComments={!!currentTask} />
                        </CardContent>
                    </>
                );
            else if(form.formStatus === FormStatus.AdvisorClarification && hasPermission(user, 'vpic.write'))
                formView = <ClarificationStage form={form} task={currentTask} onSave={refreshPage} title="Advisor Clarification Requested" />;
            else if(form.formStatus === FormStatus.ComplianceClarification && hasRole(user, UserRole.Compliance))
                formView = <ClarificationStage form={form} task={currentTask} onSave={refreshPage} title="Compliance Clarification Requested" />;
            else if(form.formStatus === FormStatus.ComplianceApproval && currentTask && hasRole(user, UserRole.Compliance))
                //formView = <VPICReimbursementComplianceForm form={form} preApprovalForm={preApprovalForm} task={currentTask} onSave={refresh} />;
                formView = (
                    <ComplianceApprovalStage form={form} task={currentTask} title="Certification" allowDealerClarification={false} onSave={refreshPage}>
                        <CardHeader>Attendance Information</CardHeader>
                        <CardContent style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
                            <h3 style={{ margin: '0' }}>Attendance</h3>
                            <Flex gap="15px">
                                <VPIDisplayTextBox label="Total Attendance (Estimate)" value={
                                    preApprovalForm ? (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees) : 'N/A'
                                } />
                                <VPIDisplayTextBox label="Total Attendance (Actual)" value={
                                    form.attendanceClients + form.attendanceEmployees
                                } />
                                <VPIDisplayTextBox label="Variance" value={
                                    preApprovalForm ? (form.attendanceClients + form.attendanceEmployees) - (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees) : 'N/A'
                                } />
                            </Flex>
                            <h3 style={{ margin: '15px 0 0 0' }}>Cost</h3>
                            <Flex gap="15px">
                                <VPIDisplayTextBox label="Total Cost (Estimate)" value={
                                    preApprovalForm ? currencyToString(preApprovalForm.expenseEstimatedTotalCost) : 'N/A'
                                } />
                                <VPIDisplayTextBox label="Total Cost (Actual)" value={currencyToString(form.amountRequested)} />
                                <VPIDisplayTextBox label="Variance" value={
                                    preApprovalForm ? currencyToString(form.amountRequested - preApprovalForm.expenseEstimatedTotalCost) : 'N/A'
                                } />
                            </Flex>
                            <h3 style={{ margin: '15px 0 0 0' }}>Cost Per Person</h3>
                            <Flex gap="15px">
                                <VPIDisplayTextBox label="Cost Per Person (Estimate)" value={
                                    preApprovalForm && (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees) > 0 ?  
                                        currencyToString(preApprovalForm.expenseEstimatedTotalCost / (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees)) :
                                        'N/A'
                                } />
                                <VPIDisplayTextBox label="Cost Per Person (Actual)" value={
                                    (form.attendanceClients + form.attendanceEmployees) > 0 ? currencyToString(form.amountRequested / (form.attendanceClients + form.attendanceEmployees)) : 'N/A'
                                } />
                                <VPIDisplayTextBox label="Variance" value={
                                    preApprovalForm 
                                        && (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees) > 0 
                                        && (form.attendanceClients + form.attendanceEmployees) > 0
                                    ? 
                                        currencyToString(
                                            (form.amountRequested / (form.attendanceClients + form.attendanceEmployees)) -
                                            (preApprovalForm.expenseEstimatedTotalCost / (preApprovalForm.attendanceClients + preApprovalForm.attendanceEmployees))
                                        )
                                    : 'N/A'
                                } />   
                            </Flex>  
                        </CardContent>
                    </ComplianceApprovalStage>
                );
            else if(form.formStatus === FormStatus.FinanceApproval && hasRole(user, UserRole.Finance))
                formView = <FinanceApprovalStage form={form} task={currentTask} onSave={refreshPage} allowDealerClarification={false} />
            else if(form.formStatus === FormStatus.Completed && hasRole(user, UserRole.Accounting))
                formView = <AccountingPaymentStage form={form} task={currentTask} onSave={refreshPage} />;
            else if(form.formStatus === FormStatus.Paid 
                || form.formStatus === FormStatus.Withdrawn 
                || form.formStatus === FormStatus.Rejected)
            {
                formView = (
                    <>
                        <CardHeader>{form.formId} | VPIC - Reimbursement Request</CardHeader>
                        <CardContent>
                            <VPICReimbursementDetails form={form} preApprovalForm={preApprovalForm} />
                        </CardContent>
                    </>
                );
            }
            else 
            {
                formView = <NoActionsStage title="VPIC Reimbursement Form" />;
            }
        }
    }

    return (
        <PageContainer type={detailsView ? '3column' : '2column'}>
            <PageHeader>
                <DefaultButton text="Back" onClick={() => nav(AppRoutes.Dashboard())} />
            </PageHeader>
            { detailsView &&
                <Card style={{ alignSelf: 'flex-start' }}>
                    <CardHeader>{formQuery.data?.formId || 'Details'}</CardHeader>
                    <CardContent>{detailsView}</CardContent>
                </Card>
            }
            <Card>{formView}</Card>
            <Card style={{ alignSelf: 'flex-start' }}>
                <CardHeader>History</CardHeader>
                <TaskHistoryList tasks={tasks?.filter(t => t.outcome && t.outcome !== TaskOutcome.InProgress)} isLoading={taskQuery.isFetching} />
            </Card>
        </PageContainer>
    );
};

